import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import { exportReport } from "@/src/services/monitoring_service";
import {
  isBusyStatus,
  markMonitoringBatchUploaded,
  markMonitoringEntryAdded,
  recheckMonitoringProcessingIfIdle,
} from "@/src/services/monitoring_processing_store";
import { useMonitoringEntries } from "./use-monitoring-entries";
import { useMonitoringKpi } from "./use-monitoring-kpi";
import {
  useMonitoringProcessingStatus,
  useOnMonitoringProcessingFinished,
} from "./use-monitoring-processing";
import { EmptyState } from "./empty-state";
import { MonitoringResults } from "./monitoring-results";
import { MonitoringLoading, MonitoringError, MonitoringProcessingBatch } from "./monitoring-states";
import { NewMonitoringModal } from "./new-monitoring-modal";
import { UploadBatchModal } from "./upload-batch-modal";
import { useTableColumnPrefs } from "@/components/use-table-column-prefs";
import { COLUMN_IDS, ENTRIES_LOCKED_FIRST, type ColumnId } from "./entries-table-utils";
import { useToast } from "@/components/action-feedback";
import type { ProcessingBadgeStatus } from "./monitoring-header";

const LIVE_REFRESH_INTERVAL_MS = 10_000;

export function MonitoringClient() {
  const ctx = useOutletContext<AppOutletContext | null>();
  const infoResolved = ctx?.infoResolved ?? false;
  const canManageEntries = !infoResolved || ctx?.apiUser?.accountType !== "auditor";
  const adverseMediaEnabled = !infoResolved || (ctx?.adverseMediaEnabled ?? true);
  const pepAllowed = !infoResolved || (ctx?.pepAllowed ?? true);
  const showToast = useToast();
  const [newOpen, setNewOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const tablePrefs = useTableColumnPrefs<ColumnId>("monitoring-entries", {
    columnIds: COLUMN_IDS,
    lockedFirst: ENTRIES_LOCKED_FIRST,
  });
  const { rowsPerPage } = tablePrefs;

  const entries = useMonitoringEntries({ infoResolved, rowsPerPage });
  const {
    kpi,
    loading: kpiLoading,
    refresh: refreshKpi,
  } = useMonitoringKpi({
    infoResolved,
    reloadKey: entries.reloadKey,
  });
  const processingStatus = useMonitoringProcessingStatus();
  const busy = isBusyStatus(processingStatus);

  // The badge only shows here — re-check on entry for work started elsewhere.
  useEffect(() => {
    recheckMonitoringProcessingIfIdle();
  }, []);

  // Table and KPIs refresh together while entries are being created/processed,
  // and once more when it finishes.
  const { refresh: refreshEntries } = entries;
  const refreshLive = useCallback(() => {
    void refreshEntries();
    refreshKpi();
  }, [refreshEntries, refreshKpi]);

  useEffect(() => {
    if (!busy) return;
    const id = setInterval(refreshLive, LIVE_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [busy, refreshLive]);

  useOnMonitoringProcessingFinished(refreshLive);

  async function handleDownloadReport() {
    const res = await exportReport("All Entries");
    if (res.ok) {
      showToast({ tone: "success", text: res.value.message });
    }
  }

  const openNew = () => setNewOpen(true);
  const openUpload = () => setUploadOpen(true);

  const handleRowsPerPageChange = (n: number) => {
    entries.setPage(1);
    tablePrefs.setRowsPerPage(n);
  };

  const { state } = entries;
  const unfiltered = entries.filter === "All Entries" && !entries.query.trim();

  // While entries are being created the table's count is the freshest total.
  const liveTotal = busy && unfiltered && state.status === "ready" ? state.count : null;
  const shownKpi = liveTotal == null ? kpi : { ...kpi, total: liveTotal };

  const badgeStatus: ProcessingBadgeStatus | undefined =
    processingStatus === "unknown" ? undefined : processingStatus;

  // Covers the gap between the backend creating entries and the list's next
  // poll picking them up, so a fresh batch never flashes EmptyState.
  const settingUpEntries = processingStatus === "creating" || (busy && unfiltered);

  let body;
  if (state.status === "loading") {
    body = (
      <MonitoringLoading
        onNewMonitoring={openNew}
        onUploadBatch={openUpload}
        onDownloadReport={handleDownloadReport}
        canManageEntries={canManageEntries}
      />
    );
  } else if (state.status === "error") {
    body = (
      <MonitoringError
        onRetry={entries.reload}
        onNewMonitoring={openNew}
        onUploadBatch={openUpload}
        onDownloadReport={handleDownloadReport}
        canManageEntries={canManageEntries}
      />
    );
  } else if (settingUpEntries && state.count === 0) {
    body = (
      <MonitoringProcessingBatch
        onNewMonitoring={openNew}
        onUploadBatch={openUpload}
        onDownloadReport={handleDownloadReport}
        canManageEntries={canManageEntries}
      />
    );
  } else if (state.count === 0 && unfiltered) {
    body = (
      <EmptyState
        onNewMonitoring={openNew}
        onUploadBatch={openUpload}
        onDownloadReport={handleDownloadReport}
        canManageEntries={canManageEntries}
      />
    );
  } else {
    const pageCount = Math.max(1, Math.ceil(state.count / rowsPerPage));
    body = (
      <MonitoringResults
        entries={state.entries}
        count={state.count}
        kpi={shownKpi}
        kpiLoading={kpiLoading}
        processingStatus={badgeStatus}
        page={Math.min(entries.page, pageCount)}
        pageCount={pageCount}
        onPageChange={entries.setPage}
        order={tablePrefs.order}
        hidden={tablePrefs.hidden}
        widths={tablePrefs.widths}
        onReorder={tablePrefs.setOrder}
        onToggleHidden={tablePrefs.toggleHidden}
        onResizeColumn={tablePrefs.setWidth}
        onResetColumns={tablePrefs.reset}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        onNewMonitoring={openNew}
        onUploadBatch={openUpload}
        canManageEntries={canManageEntries}
        onReload={entries.reload}
        query={entries.query}
        onQueryChange={entries.setQuery}
        filter={entries.filter}
        onFilterChange={entries.setFilter}
        searching={entries.searching}
      />
    );
  }

  return (
    <>
      {body}
      <NewMonitoringModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        adverseMediaEnabled={adverseMediaEnabled}
        pepAllowed={pepAllowed}
        onAdded={() => {
          setNewOpen(false);
          entries.reload();
          markMonitoringEntryAdded();
          showToast({ tone: "success", text: "The entity has been created successfully." });
        }}
      />
      <UploadBatchModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false);
          markMonitoringBatchUploaded();
          entries.reload();
          showToast({
            tone: "success",
            text: "Monitoring batch uploaded — processing will start shortly.",
          });
        }}
      />
    </>
  );
}
